import React from 'react'
import { QueryClient, QueryClientProvider } from 'react-query'
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom'
import { HelmetProvider } from 'react-helmet-async'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import { SnackbarProvider } from 'notistack'
import { StockScreen } from './pages/StockScreen'
import { StockScreenEco } from './pages/StockScreenEco'
import { DashboardScreen } from './pages/DashboardScreen'
import { LoginScreen } from './pages/LoginScreen'
import { ForgotPasswordScreen } from './pages/ForgotPasswordScreen'
import { ResetPasswordScreen } from './pages/ResetPasswordScreen'
import { CreateUserScreen } from './pages/CreateUserScreen'
import { AdminPanelScreen } from './pages/AdminPanelScreen'
import { EditUserScreen } from './pages/EditUserScreen'
import { ProfileScreen } from './pages/ProfileScreen'
import { PrivateRoute } from './component/shared/PrivateRoute'
import {
  HTL_STOCK,
  ECO_STOCK,
  ADMIN_PANEL,
  SALES_STATS,
  TLP_POINT,
  TRIP_POINT,
  NEXEN_POINT,
  STAFF_BENEFIT_ADMIN,
  TYREHUB_POINT,
  BATTERY_INCENTIVE_POINT,
  MOBIL_NORTH_CHINA_TRIP_POINT,
  FIESTA_POINT,
  CP58,
  HTG_INCENTIVE,
  HTG_INCENTIVE_ADMIN,
  WHATSAPP_BROADCAST,
  HTL_2026,
  SALES_COMMISSION_INCENTIVE,
  CNN_POINT,
  CNN_ADMIN,
  GMN_POINT,
  GMN_ADMIN,
  LUCKY_DRAW_ADMIN,
} from '../src/constant/permission'
import { UserAuthProvider } from './api/context/UserAuthContext'
import { Helmet } from 'react-helmet-async'
import { SalesStatsScreen } from './pages/SalesStatsScreen'
import { TripAccountSummaryScreen } from './pages/TripAccountSummaryScreen'
import { NexenPointPanelScreen } from './pages/NexenPointPanelScreen'
import { NexenAccountSummaryScreen } from './pages/NexenPointAccountSummaryScreen'
import { StaffBenefitPanelScreen } from './pages/StaffBenefitPanelScreen'
import { CreateStaffBenefit } from './pages/CreateStaffBenefitScreen'
import { StaffBenefitUserPanelScreen } from './pages/StaffBenefitUserPanelScreen'
import { StaffBenefitClaimPanelScreen } from './pages/StaffBenefitClaimPanelScreen'
import { StaffBenefitReviewPanelScreen } from './pages/StaffBenefitReviewPanelScreen'
import { NexenTripAccountSummaryScreen } from './pages/NexenTripPointAccountSummaryScreen'
import { NexenTripPointPanelScreen } from './pages/NexenTripPointPanelScreen'
import { TyrehubPoints } from './pages/tyrehub-point/TyrehubPoints'
import { TyrehubPoint } from './pages/tyrehub-point/TyrehubPoint'
import { BatteryIncentivePoints } from './pages/battery-incentive-point/BatteryIncentivePoints'
import { BatteryIncentivePoint } from './pages/battery-incentive-point/BatteryIncentivePoint'
import { MobilNorthChinaTripPoint } from './pages/trip-north-point/MobilNorthChinaTripPoint'
import { MobilNorthChinaTripPoints } from './pages/trip-north-point/MobilNorthChinaTripPoints'
import { HTL2026Points } from './pages/htl-2026/HTL2026Points'
import { HTL2026Point } from './pages/htl-2026/HTL2026Point'
import { FiestaPoints } from './pages/fiesta-point/FiestaPoints'
import { Commission } from './pages/sales-commission/Page'
import { WinnerPicker } from './pages/lucky-draw/Page'
import { TLPPoints } from './pages/tlp-point/TLPPoints'
import { TLPPoint } from './pages/tlp-point/TLPPoint'
import { HTGIncentivePoint } from './pages/htg-incentive/HTGIncentivePoint'
import { HTGIncentivePoints } from './pages/htg-incentive/HTGIncentivePoints'
import { CP58Screen } from './pages/cp-58/Page'
import { HtgIncentiveAdminScreen } from './pages/htg-incentive-admin/Page'
import { WhatsappConfigScreen } from './pages/whatsapp-config/Page'
import { WhatsappBroadcastScreen } from './pages/whatsapp-broadcast/Page'
import { WhatsappBroadcastProgressScreen } from './pages/whatsapp-broadcast/ProgressPage'
import { MobilChinaTripPoints } from './pages/mobil-china-trip-point/MobilChinaTripPoints'
import { SalesRecipientPage } from './pages/sales-commission-incentive/recipient/Page'
import { SalesIncentivePage } from './pages/sales-commission-incentive/incentive/Page'
import { SalesCommissionIncentiveHub } from './pages/sales-commission-incentive/HubPage'
import { Payout } from './pages/sales-commission-incentive/payout/Page'
import { SalesVisualizationPage } from './pages/sales-commission-incentive/visualization/Page'
import { CnnPage } from './pages/cnn/Page'
import { CnnPointsPage } from './pages/cnn-points/Page'
import { CnnPointsDetailPage } from './pages/cnn-points-detail/Page'
import { GmnPage } from './pages/gmn/Page'
import { GmnPointsPage } from './pages/gmn-points/Page'
import { GmnPointsDetailPage } from './pages/gmn-points-detail/Page'
import { LuckyDrawAdminPage } from './pages/lucky-draw-admin/Page'
import { LuckyDrawCreatePage } from './pages/lucky-draw-form/Create'
import { LuckyDrawEditPage } from './pages/lucky-draw-form/Edit'
import { LuckyDrawRollPage } from './pages/lucky-draw-roll/Page'

const queryClient = new QueryClient()

const theme = createTheme({
  palette: {
    primary: {
      main: '#000',
    },
    secondary: {
      main: '#fff',
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
    holiday: {
      main: '#4caf50',
    },
  },
  typography: {
    h1: {
      fontSize: '26px',
      fontWeight: '500',
    },
    h2: {
      fontSize: '1.1rem',
      fontWeight: '600',
      color: '#222222',
    },
    h3: {
      fontSize: '25px',
      fontWeight: '200',
      color: '#565656',
    },
    h4: {
      fontSize: '18px',
      fontWeight: '500',
      color: '#323232',
    },
    h5: {
      fontSize: '18px',
      fontWeight: '500',
    },
    subtitle1: {
      fontSize: '22px',
      fontWeight: '300',
    },
    subtitle2: {
      fontSize: '8px',
      fontWeight: '500',
    },
    button: {
      fontWeight: '500',
    },
  },
})

const App = () => {
  const routes = [
    { path: '/', component: DashboardScreen },
    { path: '/htl-stocklist', component: StockScreen, permission: HTL_STOCK },
    { path: '/eco-stocklist', component: StockScreenEco, permission: ECO_STOCK },
    { path: '/create-user', component: CreateUserScreen, permission: ADMIN_PANEL },
    { path: '/edit-user', component: EditUserScreen, permission: ADMIN_PANEL },
    { path: '/admin', component: AdminPanelScreen, permission: ADMIN_PANEL },
    { path: '/staff-benefit', component: StaffBenefitPanelScreen, permission: STAFF_BENEFIT_ADMIN },
    {
      path: '/create-staff-benefit',
      component: CreateStaffBenefit,
      permission: STAFF_BENEFIT_ADMIN,
    },
    { path: '/user-staff-benefit', component: StaffBenefitUserPanelScreen },
    { path: '/staff-benefit-claims', component: StaffBenefitClaimPanelScreen },
    { path: '/claims-review', component: StaffBenefitReviewPanelScreen },
    // { path: '/company-mapping', component: CompanyMappingScreen },
    {
      path: '/htg-incentive-admin',
      component: HtgIncentiveAdminScreen,
      permission: HTG_INCENTIVE_ADMIN,
    },
    {
      path: '/whatsapp-config',
      component: WhatsappConfigScreen,
      permission: ADMIN_PANEL,
    },
    {
      path: '/whatsapp-broadcast',
      component: WhatsappBroadcastScreen,
      permission: WHATSAPP_BROADCAST,
    },
    {
      path: '/whatsapp-broadcast-progress/:jobId',
      component: WhatsappBroadcastProgressScreen,
      permission: WHATSAPP_BROADCAST,
    },
    { path: '/my-profile', component: ProfileScreen },
    { path: '/cp-58', component: CP58Screen, permission: CP58 },
    { path: '/revenue-dashboard', component: SalesStatsScreen, permission: SALES_STATS },
    { path: '/tlp-point', component: TLPPoints, permission: TLP_POINT },
    { path: '/tlp-summary', component: TLPPoint, permission: TLP_POINT },
    { path: '/trip-point', component: MobilChinaTripPoints, permission: TRIP_POINT },
    { path: '/trip-summary', component: TripAccountSummaryScreen, permission: TRIP_POINT },
    { path: '/nexen-point', component: NexenPointPanelScreen, permission: NEXEN_POINT },
    { path: '/nexen-summary', component: NexenAccountSummaryScreen, permission: NEXEN_POINT },
    { path: '/nexen-trip-point', component: NexenTripPointPanelScreen, permission: NEXEN_POINT },
    {
      path: '/nexen-trip-summary',
      component: NexenTripAccountSummaryScreen,
      permission: NEXEN_POINT,
    },
    { path: '/tyrehub-trip-point', component: TyrehubPoints, permission: TYREHUB_POINT },
    { path: '/tyrehub-trip-summary', component: TyrehubPoint, permission: TYREHUB_POINT },
    {
      path: '/battery-incentive-point',
      component: BatteryIncentivePoints,
      permission: BATTERY_INCENTIVE_POINT,
    },
    {
      path: '/battery-incentive-summary',
      component: BatteryIncentivePoint,
      permission: BATTERY_INCENTIVE_POINT,
    },
    {
      path: '/mobil-north-china-trip-point',
      component: MobilNorthChinaTripPoints,
      permission: MOBIL_NORTH_CHINA_TRIP_POINT,
    },
    {
      path: '/mobil-north-china-trip-summary',
      component: MobilNorthChinaTripPoint,
      permission: MOBIL_NORTH_CHINA_TRIP_POINT,
    },
    {
      path: '/htl-2026-point',
      component: HTL2026Points,
      permission: HTL_2026,
    },
    {
      path: '/htl-2026-summary',
      component: HTL2026Point,
      permission: HTL_2026,
    },
    { path: '/fiesta-point', component: FiestaPoints, permission: FIESTA_POINT },
    { path: '/fiesta-lucky-draw', component: WinnerPicker },
    {
      path: '/sales-commission',
      component: Commission,
      permission: SALES_COMMISSION_INCENTIVE,
    },
    {
      path: '/sales-payout',
      component: Payout,
      permission: SALES_COMMISSION_INCENTIVE,
    },
    {
      path: '/sales-comission-incentive',
      component: SalesCommissionIncentiveHub,
      permission: SALES_COMMISSION_INCENTIVE,
    },
    {
      path: '/sales-visualization',
      component: SalesVisualizationPage,
      permission: SALES_COMMISSION_INCENTIVE,
    },
    {
      path: '/htg-incentive-point',
      component: HTGIncentivePoints,
      permission: HTG_INCENTIVE,
    },
    {
      path: '/htg-incentive-summary',
      component: HTGIncentivePoint,
      permission: HTG_INCENTIVE,
    },
    {
      path: '/sales-recipient',
      component: SalesRecipientPage,
      permission: SALES_COMMISSION_INCENTIVE,
    },
    {
      path: '/sales-incentive',
      component: SalesIncentivePage,
      permission: SALES_COMMISSION_INCENTIVE,
    },
    {
      path: '/ht-connect',
      component: CnnPage,
      permission: CNN_ADMIN,
    },
    {
      path: '/ht-connect/points',
      component: CnnPointsPage,
      permission: CNN_POINT,
    },
    {
      path: '/ht-connect/points/:customerId',
      component: CnnPointsDetailPage,
      permission: CNN_POINT,
    },
    {
      path: '/gmn',
      component: GmnPage,
      permission: GMN_ADMIN,
    },
    {
      path: '/gmn/points',
      component: GmnPointsPage,
      permission: GMN_POINT,
    },
    {
      path: '/gmn/points/:customerId',
      component: GmnPointsDetailPage,
      permission: GMN_POINT,
    },
    {
      path: '/lucky-draw',
      component: LuckyDrawAdminPage,
      permission: LUCKY_DRAW_ADMIN,
    },
    {
      path: '/lucky-draw/create',
      component: LuckyDrawCreatePage,
      permission: LUCKY_DRAW_ADMIN,
    },
    {
      path: '/lucky-draw/:id/edit',
      component: LuckyDrawEditPage,
      permission: LUCKY_DRAW_ADMIN,
    },
    {
      path: '/lucky-draw/:id/roll',
      component: LuckyDrawRollPage,
      permission: LUCKY_DRAW_ADMIN,
    },
  ]

  return (
    <HelmetProvider>
      <Helmet defaultTitle='Staff Portal'>
        <title>Staff Portal</title>
      </Helmet>
      <SnackbarProvider>
        <Router>
          <QueryClientProvider client={queryClient}>
            <ThemeProvider theme={theme}>
              <UserAuthProvider>
                <Routes>
                  {routes.map(({ path, component: Component, permission }) => (
                    <Route
                      key={path}
                      path={path}
                      element={
                        <PrivateRoute permission={permission}>
                          <Component />
                        </PrivateRoute>
                      }
                    />
                  ))}
                  <Route path='/login' element={<LoginScreen />} />
                  <Route path='/forgot-password' element={<ForgotPasswordScreen />} />
                  <Route path='/reset-password' element={<ResetPasswordScreen />} />
                </Routes>
              </UserAuthProvider>
            </ThemeProvider>
          </QueryClientProvider>
        </Router>
      </SnackbarProvider>
    </HelmetProvider>
  )
}

export default App
